// One styled confirmation dialog for every destructive / scope-affecting action,
// replacing the native window.confirm() (off-design, not theme-aware, not
// focus-trapped) and the unguarded one-click deactivate/discard toggles. Call
// sites use `const confirm = useConfirm()` then `if (await confirm({...})) ...`,
// mirroring window.confirm's boolean contract but rendered as an AGE MudModal.
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudModal } from '@egov-moldova/mud-react';
import { onModalDismiss } from '@simsm/ui-shared';

interface ConfirmOptions {
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used within ConfirmProvider');
  return confirm;
}

export function ConfirmProvider({ children }: PropsWithChildren): JSX.Element {
  const { t } = useTranslate();
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (next) =>
      new Promise<boolean>((resolve) => {
        // If a prior confirm is somehow still pending, settle it false rather
        // than orphan its awaiting caller.
        resolverRef.current?.(false);
        resolverRef.current = resolve;
        setOptions(next);
      }),
    [],
  );

  const settle = useCallback((value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setOptions(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <MudModal
        closeLabel={t('common.close')}
        open={options !== null}
        size="sm"
        destructive={options?.destructive ?? false}
        titleText={options?.title ?? ''}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — seven AGE
        // components emit `mudClose` bubbling+composed, so a descendant closing inside this
        // modal reaches this handler too; the guard answers only a real dismissal.
        onMudClose={onModalDismiss(() => settle(false))}
      >
        {options ? <div className="simsm-confirm__body">{options.body}</div> : null}
        <div slot="actions" className="simsm-confirm__actions">
          <MudButton shape="circular" variant="neutral" size="md" onClick={() => settle(false)}>
            {options?.cancelLabel ?? t('common.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant={options?.destructive ? 'destructive' : 'primary'}
            size="md"
            onClick={() => settle(true)}
          >
            {options?.confirmLabel ?? t('common.confirm')}
          </MudButton>
        </div>
      </MudModal>
    </ConfirmContext.Provider>
  );
}
